import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Ban, CheckCircle2, Trash2, Users, KeyRound, Eye, EyeOff } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const ROLES = ["user", "instructor", "admin"];

const TIER_BADGE = {
  comp: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  team: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  pro: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  trial: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  free: "border-slate-600 bg-slate-700/40 text-slate-300",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [pwTarget, setPwTarget] = useState(null); // password-rotation dialog target
  const [pwValue, setPwValue] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/api/admin/users").then(({ data }) => setUsers(data.users)).catch(() => setUsers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    if (role === u.role) return;
    setBusy(u.id);
    try {
      await api.patch(`/api/admin/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${role}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const setStatus = async (u, status) => {
    setBusy(u.id);
    try {
      await api.patch(`/api/admin/users/${u.id}/status`, { status });
      toast.success(`${u.email} ${status === "disabled" ? "disabled" : "re-activated"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const setComp = async (u) => {
    setBusy(u.id);
    try {
      await api.patch(`/api/admin/users/${u.id}/comp`, { comp: !u.comp });
      toast.success(!u.comp ? `${u.email} now has a free (comp) account.` : `Removed comp access for ${u.email}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const remove = async () => {
    const u = toDelete;
    setToDelete(null);
    setBusy(u.id);
    try {
      await api.delete(`/api/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const openPwDialog = (u) => {
    setPwTarget(u);
    setPwValue("");
    setPwConfirm("");
    setPwShow(false);
  };

  const closePwDialog = () => {
    if (pwBusy) return;
    setPwTarget(null);
    setPwValue("");
    setPwConfirm("");
    setPwShow(false);
  };

  const submitPwReset = async () => {
    if (!pwTarget) return;
    if (pwValue.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (pwValue !== pwConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post(`/api/admin/users/${pwTarget.id}/password`, { new_password: pwValue });
      toast.success(`Password rotated for ${pwTarget.email}. All their sessions have been revoked.`);
      setPwTarget(null);
      setPwValue("");
      setPwConfirm("");
      setPwShow(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <Users className="w-3.5 h-3.5" /> User management
      </div>
      <h1 className="text-2xl font-bold text-white">Registered users</h1>
      <p className="text-sm text-slate-400 mt-1">Assign roles, disable accounts, or remove them.</p>

      <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] overflow-hidden">
        {users === null ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="text-center text-slate-500 py-16 text-sm">No users found.</div>
        ) : (
          <div className="divide-y divide-slate-800" data-testid="admin-users-list">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <span className="col-span-3">User</span>
              <span className="col-span-2">Role</span>
              <span className="col-span-1">Sessions</span>
              <span className="col-span-2">Joined</span>
              <span className="col-span-4 text-right">Actions</span>
            </div>
            {users.map((u) => {
              const isMe = me?.id === u.id;
              const disabled = u.status === "disabled";
              return (
                <div key={u.id} className={`grid grid-cols-12 gap-3 px-4 py-3 items-center ${disabled ? "opacity-60" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                  <div className="col-span-12 md:col-span-3 min-w-0">
                    <div className="text-sm font-medium text-slate-100 truncate flex items-center gap-1.5">
                      {u.name || "—"} {isMe && <span className="text-[10px] text-[#5ec0ff]">(you)</span>}
                      {disabled && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">DISABLED</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {u.email}
                      <span className="ml-1.5 text-[10px] text-slate-600">· {u.auth_provider}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase ${TIER_BADGE[u.tier] || TIER_BADGE.free}`} data-testid={`user-tier-${u.id}`}>
                        {u.tier}
                      </span>
                      {u.team && <span className="text-[10px] text-slate-500 truncate">{u.team}</span>}
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <select
                      value={u.role}
                      disabled={isMe || busy === u.id}
                      onChange={(e) => setRole(u, e.target.value)}
                      data-testid={`admin-role-select-${u.id}`}
                      className="bg-[#0b1220] border border-slate-700 text-slate-200 text-xs rounded-md px-2 py-1.5 focus:border-[#5ec0ff] outline-none disabled:opacity-50 capitalize"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r} className="bg-[#0b1220]">{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-3 md:col-span-1 text-sm text-slate-300 font-mono-clinical">{u.session_count}</div>
                  <div className="col-span-3 md:col-span-2 text-xs text-slate-500">{fmtDate(u.created_at)}</div>
                  <div className="col-span-12 md:col-span-4 flex md:justify-end gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" disabled={busy === u.id} onClick={() => setComp(u)}
                      data-testid={`admin-comp-${u.id}`}
                      className={`text-xs bg-transparent ${u.comp ? "border-violet-500/50 text-violet-300 hover:bg-violet-500/10" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                      {u.comp ? "Revoke free" : "Grant free"}
                    </Button>
                    <Button size="sm" variant="outline"
                      disabled={busy === u.id || (u.auth_provider === "google" && !u.password_hash)}
                      onClick={() => openPwDialog(u)}
                      data-testid={`admin-reset-password-${u.id}`}
                      className="text-xs bg-transparent border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                      title={u.auth_provider === "google" ? "Google-only account — nothing to rotate" : "Rotate this user's password"}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Rotate PW
                    </Button>
                    {disabled ? (
                      <Button size="sm" variant="outline" disabled={busy === u.id} onClick={() => setStatus(u, "active")}
                        data-testid={`admin-enable-${u.id}`}
                        className="bg-transparent border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10 text-xs">
                        {busy === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enable</>}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={isMe || busy === u.id} onClick={() => setStatus(u, "disabled")}
                        data-testid={`admin-disable-${u.id}`}
                        className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 text-xs">
                        {busy === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Ban className="w-3.5 h-3.5 mr-1" /> Disable</>}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" disabled={isMe || busy === u.id} onClick={() => setToDelete(u)}
                      data-testid={`admin-delete-${u.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>{toDelete?.email}</strong> and all of their saved sessions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-delete-confirm" className="bg-rose-600 hover:bg-rose-700">
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && closePwDialog()}>
        <DialogContent data-testid="admin-reset-password-dialog" className="bg-[#0e1726] border border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Rotate password for this user</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a new password for <strong className="text-slate-200">{pwTarget?.email}</strong>. All of their active sessions and refresh tokens will be revoked immediately — they'll need to sign back in on every device.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">New password</label>
              <div className="relative mt-1">
                <Input
                  type={pwShow ? "text" : "password"}
                  value={pwValue}
                  onChange={(e) => setPwValue(e.target.value)}
                  autoComplete="new-password"
                  data-testid="admin-reset-password-input"
                  className="bg-[#0b1220] border-slate-700 text-slate-100 pr-9"
                  placeholder="Minimum 8 chars, non-breached"
                />
                <button
                  type="button"
                  onClick={() => setPwShow((v) => !v)}
                  data-testid="admin-reset-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                  aria-label={pwShow ? "Hide password" : "Show password"}
                >
                  {pwShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Confirm new password</label>
              <Input
                type={pwShow ? "text" : "password"}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="admin-reset-password-confirm"
                className="bg-[#0b1220] border-slate-700 text-slate-100 mt-1"
                placeholder="Re-enter to confirm"
              />
            </div>
            <p className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-md px-2.5 py-1.5">
              Same policy as user-facing reset: min 8 chars, not on the common-password list, and not present in the HIBP breach corpus.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closePwDialog} disabled={pwBusy}
              data-testid="admin-reset-password-cancel" className="text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={submitPwReset} disabled={pwBusy || !pwValue || !pwConfirm}
              data-testid="admin-reset-password-confirm-btn"
              className="bg-amber-600 hover:bg-amber-500 text-white">
              {pwBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
              Rotate password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
